import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, CheckCircle2, AlertTriangle, XCircle, FileText, Sparkles,
  Database, AudioLines, ShieldOff, Info, FileCheck2, Hash,
  Aperture, Scan, Layers, FileSignature, KeyRound, Activity, Mic2, Waves,
  Copy, Check, Download, Mail, Lock, Loader2, Eye, Send,
  X, Settings, ChevronRight, Clock, Thermometer, PanelLeft,
  HelpCircle, Zap, Globe, GitMerge, Filter, Shield, Cpu, TriangleAlert,
  GitCompareArrows, Paintbrush, Scale,
} from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../api/client";
import { TruthMeter } from "../components/TruthMeter";
import { ProAccessModal } from "../components/ProAccessModal";

const verdictMap = {
  safe:                { label: "SEGURO",          color: "#8BF56C", bg: "rgba(139,245,108,0.07)",  border: "rgba(139,245,108,0.28)",  Icon: CheckCircle2, badge: "Autenticidad probable" },
  consistent:          { label: "CONSISTENTE",     color: "#8BF56C", bg: "rgba(139,245,108,0.07)",  border: "rgba(139,245,108,0.28)",  Icon: CheckCircle2, badge: "Captura nativa verificada" },
  suspicious:          { label: "SOSPECHOSO",      color: "#F4D03F", bg: "rgba(244,208,63,0.07)",   border: "rgba(244,208,63,0.28)",   Icon: AlertTriangle, badge: "Requiere revisión" },
  inconclusive:        { label: "INCONCLUYENTE",   color: "#F4B942", bg: "rgba(244,185,66,0.07)",   border: "rgba(244,185,66,0.28)",   Icon: HelpCircle,    badge: "Revisión humana recomendada" },
  inconclusive_network:{ label: "PENDIENTE",       color: "#F4B942", bg: "rgba(244,185,66,0.07)",   border: "rgba(244,185,66,0.28)",   Icon: HelpCircle,    badge: "APIs no disponibles · Repetir análisis" },
  manipulated:         { label: "MANIPULADO",      color: "#FF5A6E", bg: "rgba(255,90,110,0.07)",   border: "rgba(255,90,110,0.28)",   Icon: XCircle,       badge: "Alta probabilidad de fraude" },
  ai_generated:        { label: "GENERADO POR IA", color: "#FF5A6E", bg: "rgba(255,90,110,0.07)",   border: "rgba(255,90,110,0.28)",   Icon: XCircle,       badge: "Imagen sintética · No fotográfica" },
};

const mediaTypeLabel = {
  image:    "Imagen / Foto",
  video:    "Video",
  audio:    "Audio",
  document: "Documento PDF",
};

// Map generic backend breakdown values to media-type-specific forensic vectors.
// Backend returns: ai_brushstrokes, corrupt_metadata, synthetic_audio (all 0-100).
function getBreakdownForMediaType(scan) {
  const b = scan.breakdown || {};
  const { ai_brushstrokes: a = 0, corrupt_metadata: m = 0, synthetic_audio: s = 0 } = b;
  switch (scan.media_type) {
    case "video":
      return [
        { icon: Activity,   label: "Biometría facial activa",     value: a, hint: "Consistencia de patrones biométricos" },
        { icon: Scan,       label: "Fluidez de movimiento",       value: s, hint: "Parpadeo natural y micro-gestos" },
        { icon: Database,   label: "Metadatos de captura",        value: m, hint: "Timestamps y firma de dispositivo" },
      ];
    case "audio":
      return [
        { icon: Mic2,       label: "Clonación de voz",            value: a, hint: "Huella acústica sintética" },
        { icon: Waves,      label: "Artefactos espectrales",      value: s, hint: "Patrones de síntesis TTS" },
        { icon: Database,   label: "Metadatos del archivo",       value: m, hint: "Codec y trazabilidad" },
      ];
    case "document":
      return [
        { icon: Layers,     label: "Estructura de capas",         value: a, hint: "Objetos anómalos en el PDF" },
        { icon: FileSignature, label: "Modificaciones de texto",  value: s, hint: "Regiones editadas sobre el original" },
        { icon: KeyRound,   label: "Integridad criptográfica",    value: m, hint: "Checksum y consistencia SHA-256" },
      ];
    case "image":
    default:
      return [
        { icon: Aperture,   label: "Ruido de sensor",             value: a, hint: "PRNU y patrón del sensor CMOS" },
        { icon: Sparkles,   label: "Artefactos de compresión",    value: s, hint: "Inconsistencias JPEG / resampling" },
        { icon: Database,   label: "Metadatos de captura",        value: m, hint: "EXIF y cadena de custodia" },
      ];
  }
}

function SidePanel({ title, subtitle, icon: Icon, defaultOpen = false, children, accent = "#DEFF9A" }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-white/[0.05]">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.025] transition-colors"
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <Icon size={13} style={{ color: accent }} className="shrink-0" />
          <div className="text-left min-w-0">
            <div className="text-[11px] font-semibold text-white truncate">{title}</div>
            {subtitle && (
              <div className="text-[9px] font-mono uppercase tracking-[0.15em] text-[#5A5F68] mt-0.5">
                {subtitle}
              </div>
            )}
          </div>
        </div>
        <ChevronRight
          size={12}
          className="text-[#3A3F46] shrink-0 transition-transform duration-200"
          style={{ transform: open ? "rotate(90deg)" : "rotate(0deg)" }}
        />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

export default function Results() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [scan, setScan] = useState(null);
  const [showAccess, setShowAccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [unlockEmail, setUnlockEmail] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [reportEmail, setReportEmail] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSentTo, setEmailSentTo] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const MAX_ATTEMPTS = 45; // ~90s with 2s interval

    const hasFinalResult = (data) => {
      if (!data) return false;
      const status = String(data?.status || "").toLowerCase();
      if (status === "completed" || status === "failed" || status === "error") return true;
      return typeof data.truthfulness_score === "number" && typeof data.verdict === "string";
    };

    const fetchScan = async () => {
      try {
        const { data } = await api.get(`/scans/${id}`);
        if (cancelled) return;
        setScan(data);

        if (!hasFinalResult(data) && attempts < MAX_ATTEMPTS) {
          attempts += 1;
          timer = setTimeout(fetchScan, 2000);
        }
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.status;
        // Supabase propagation can briefly return 404 right after create.
        // Retry transient errors instead of kicking the user back to /app.
        if ((code === 404 || code >= 500 || !code) && attempts < MAX_ATTEMPTS) {
          attempts += 1;
          timer = setTimeout(fetchScan, 2000);
          return;
        }
        navigate("/app");
      }
    };

    fetchScan();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [id, navigate]);

  const copyHash = async () => {
    if (!scan?.file_hash) return;
    try {
      await navigator.clipboard.writeText(scan.file_hash);
      setCopied(true);
      toast.success("Hash SHA-256 copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  const requestPdfByEmail = () => {
    const el = document.querySelector('[data-testid="email-report-block"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const downloadPdfDirect = () => {
    const token = typeof window !== "undefined" ? localStorage.getItem("dc_token") : null;
    if (!token) return toast.error("Sesión expirada");
    const url = `${API}/scans/${id}/report.pdf?auth=${token}`;
    const a = document.createElement("a");
    a.href = url;
    a.download = `ScanEye_Informe_${(scan?.forensic_ref || id?.slice(0,8) || "").toUpperCase()}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const downloadCertificateTxt = () => {
    if (!scan?.certificate_text) return;
    const blob = new Blob([scan.certificate_text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ScanEye_Certificado_${scan.id.slice(0, 8)}.txt`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!unlockEmail || !unlockEmail.includes("@")) {
      toast.error("Introduce un email válido");
      return;
    }
    setUnlocking(true);
    try {
      const { data } = await api.post(`/scans/${id}/unlock`, { email: unlockEmail });
      setScan(data);
      setReportEmail(unlockEmail);
      toast.success("Acceso desbloqueado · Gracias por validar tu email");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo desbloquear");
    } finally {
      setUnlocking(false);
    }
  };

  const handleSendEmailReport = async (e) => {
    e.preventDefault();
    if (!reportEmail || !reportEmail.includes("@")) {
      toast.error("Introduce un email válido profesional");
      return;
    }
    setSendingEmail(true);
    try {
      await api.post(`/scans/${id}/email-report`, { email: reportEmail });
      setEmailSentTo(reportEmail);
      toast.success("Solicitud recibida", {
        description: "El equipo pericial de ScanEye procesará su informe y le contactará.",
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la solicitud");
    } finally {
      setSendingEmail(false);
    }
  };

  if (!scan) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="fixed inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.015) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.015) 1px,transparent 1px)', backgroundSize: '72px 72px' }} />
        <div className="relative flex flex-col items-center gap-5">
          <div className="relative flex items-center justify-center" style={{ width: 80, height: 80 }}>
            <div className="scan-ring absolute rounded-full" style={{ width: 44, height: 44, border: '1px solid rgba(0,87,255,0.4)' }} />
            <div className="scan-ring-2 absolute rounded-full" style={{ width: 44, height: 44, border: '1px solid rgba(0,87,255,0.25)' }} />
            <div className="absolute inset-0 flex items-center justify-center">
              <Loader2 size={22} className="text-[#0057FF] animate-spin" />
            </div>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/25">
            Cargando análisis forense...
          </div>
        </div>
      </div>
    );
  }

  const v = verdictMap[scan.verdict] || verdictMap.safe;
  const VerdictIcon = v.Icon;
  const ts = new Date(scan.created_at);
  const manipulation = 100 - scan.truthfulness_score;
  const breakdown = getBreakdownForMediaType(scan);
  const token = typeof window !== "undefined" ? localStorage.getItem("dc_token") : null;
  const fileUrl = (path) => path && token ? `${API}/files?path=${encodeURIComponent(path)}&auth=${token}` : null;
  const originalUrl = scan.media_type === "image" ? fileUrl(scan.storage_path) : null;
  const elaUrl = fileUrl(scan.ela_path);
  const heatmapUrl = fileUrl(scan.heatmap_path);
  const exifEntries = (() => {
    if (!scan.exif_metadata) return { status: null, items: [] };
    if (scan.exif_metadata._status) return { status: scan.exif_metadata._status, items: [] };
    const priority = ["Make", "Model", "DateTime", "DateTimeOriginal", "Software",
                      "ExposureTime", "FNumber", "ISOSpeedRatings", "FocalLength",
                      "Orientation", "ImageWidth", "ImageLength", "GPSInfo"];
    const entries = Object.entries(scan.exif_metadata);
    const sorted = [
      ...entries.filter(([k]) => priority.includes(k)).sort((a, b) => priority.indexOf(a[0]) - priority.indexOf(b[0])),
      ...entries.filter(([k]) => !priority.includes(k)),
    ];
    return { status: null, items: sorted };
  })();
  const isLocked = scan.media_type === "image" && !scan.unlocked;

  const MetaTable = ({ rows }) => (
    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-[11px]">
      {rows.map(([k, val]) => (
        <React.Fragment key={k}>
          <dt className="font-mono uppercase tracking-[0.1em] text-[#5A5F68]">{k}</dt>
          <dd className="font-mono text-[#C8CDD5] break-all">{val}</dd>
        </React.Fragment>
      ))}
    </dl>
  );

  const modeAccent = scan.analysis_mode === "legal" ? "#818CF8" : "#DEFF9A";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Subtle grid */}
      <div className="fixed inset-0 pointer-events-none" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,.015) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.015) 1px,transparent 1px)', backgroundSize: '72px 72px' }} />

      {/* Ambient verdict-colored orbs */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
        <div className="verdict-glow orb-float absolute rounded-full" style={{ width: 640, height: 640, top: -220, right: -160, background: `radial-gradient(circle, ${v.color}09 0%, transparent 65%)` }} />
        <div className="verdict-glow orb-float-2 absolute rounded-full" style={{ width: 500, height: 500, bottom: -160, left: -100, background: `radial-gradient(circle, ${v.color}06 0%, transparent 65%)` }} />
      </div>

      {/* ── TOP BAR ── */}
      <header className="sticky top-0 z-20 border-b border-white/[0.06] bg-[#0a0a0a]/90 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-13 flex items-center justify-between gap-4 py-3">
          <button data-testid="results-back-btn" onClick={() => navigate("/app")}
            className="flex items-center gap-2 text-white/40 hover:text-white transition-colors text-[13px]">
            <ArrowLeft size={15} /> Nuevo análisis
          </button>
          <div className="flex items-center gap-2.5">
            <span className="text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 rounded-full border"
              style={{ color: modeAccent, borderColor: `${modeAccent}30`, background: `${modeAccent}08` }}>
              {scan.analysis_mode === "legal" ? "Área Legal" : "Área Verify"}
            </span>
            <span className="text-[10px] font-mono text-white/25 hidden sm:block">REF·{(scan.forensic_ref || scan.id.slice(0,8)).toUpperCase()}</span>
          </div>
        </div>
      </header>

      {/* ── MAIN LAYOUT ── */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-8 pb-20">
        <div className="grid lg:grid-cols-[1fr_360px] xl:grid-cols-[1fr_400px] gap-8 items-start">

          {/* ══════ LEFT — Main content ══════ */}
          <div className="space-y-5 min-w-0">

            {/* Verdict banner */}
            {!isLocked && (
              <div data-testid="verdict-banner"
                className="rounded-2xl p-6 border flex flex-col sm:flex-row sm:items-center gap-5 relative overflow-hidden"
              style={{ background: v.bg, borderColor: v.border, boxShadow: `0 0 40px ${v.color}10` }}>
                <div className="flex items-center gap-4 flex-1">
                  <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `${v.color}15`, border: `1px solid ${v.color}30` }}>
                    <VerdictIcon size={24} style={{ color: v.color }} />
                  </div>
                  <div className="flex-1">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-white/40 mb-0.5">Diagnóstico forense</div>
                    <div className="font-display text-2xl sm:text-3xl font-bold leading-tight" style={{ color: v.color }}>{v.label}</div>
                    <div className="text-[12px] text-white/50 mt-0.5">{v.badge}</div>
                    {scan.verdict === 'inconclusive' && (
                      <div className="mt-2 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-[#F4B942]/70">
                        <HelpCircle size={10} />
                        <span>Los detectores están en conflicto · Aporta el archivo original</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="sm:text-right shrink-0">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-white/35 mb-0.5">Manipulación</div>
                  <div className="font-display text-5xl font-bold leading-none" style={{ color: v.color }}>
                    {manipulation}<span className="text-2xl">%</span>
                  </div>
                </div>
              </div>
            )}

            {/* Recommendation */}
            {!isLocked && scan.operational_recommendation && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-5">
                <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: modeAccent }}>Recomendación operativa</div>
                <p className="text-[14px] text-white/70 leading-relaxed">{scan.operational_recommendation}</p>
              </div>
            )}

            {/* Truth meter + narrative */}
            {!isLocked && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-6 flex flex-col items-center">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/30 mb-4">Índice de veracidad ponderado</div>
                <TruthMeter score={scan.truthfulness_score} verdict={scan.verdict} />
                {scan.narrative && (
                  <div data-testid="narrative-verdict" className="mt-5 w-full rounded-xl p-4 border" style={{ background: v.bg, borderColor: v.border }}>
                    <div className="text-[9px] font-mono uppercase tracking-widest mb-1" style={{ color: v.color }}>Conclusión técnica</div>
                    <p className="text-[13px] leading-relaxed" style={{ color: v.color }}>{scan.narrative}</p>
                  </div>
                )}
                <p className="text-center text-[12px] text-white/40 max-w-md mt-3 leading-relaxed">{scan.summary}</p>
                {scan.breakdown?.weighted_components && (() => {
                  const comps = scan.breakdown.weighted_components;
                  const w = comps.weights || {};
                  const cols = [
                    { label: "Sightengine", value: comps.sightengine_veracity, weight: w.sightengine },
                    { label: "ELA",         value: comps.ela_veracity,         weight: w.ela },
                    ...(w.gemini > 0 && comps.gemini_veracity != null
                      ? [{ label: "Gemini IA", value: comps.gemini_veracity, weight: w.gemini }]
                      : []),
                    { label: "EXIF",        value: comps.exif_veracity,       weight: w.exif },
                  ];
                  return (
                    <div className={`grid gap-2 mt-5 w-full grid-cols-${cols.length}`}>
                      {cols.map(c => (
                        <div key={c.label} className="bg-white/[0.03] border border-white/[0.05] rounded-xl p-3 text-center">
                          <div className="text-[9px] font-mono uppercase tracking-widest text-white/30 mb-1">{c.label}<br />{Math.round((c.weight||0)*100)}%</div>
                          <div className="font-mono text-lg font-bold text-white">{Math.round(c.value ?? 0)}%</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {(() => {
                  const c = scan.breakdown?.weighted_components || {};
                  const penalty = c.penalty_applied;
                  const penaltyConfig = {
                    light_10:           { color: "#8BF56C",  icon: "✓", text: "Penalización leve \u221210% (IA confirma autenticidad)" },
                    severe_30:          { color: "#F4D03F",  icon: "⚠", text: "Penalización severa \u221230% (EXIF ausente, fuente desconocida)" },
                    minimal_web_format: { color: "#9AD7FF",  icon: "ℹ", text: "Penalización m\u00ednima \u22123% \u2014 EXIF ausente es normal en archivos web/mensajer\u00eda" },
                    neutralized_ui_protocol: { color: "#8BF56C", icon: "✓", text: "Sin penalización EXIF \u2014 protocolo UI nativo (captura de pantalla)" },
                  };
                  const pc = penaltyConfig[penalty];
                  return pc ? (
                    <div data-testid="penalty-note" className="mt-3 flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest" style={{ color: pc.color }}>
                      <span>{pc.icon}</span><span>{pc.text}</span>
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const c = scan.breakdown?.weighted_components || {};
                  const flags = c.flags || [];
                  return (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {c.web_compressed && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#9AD7FF]/10 border border-[#9AD7FF]/20 text-[9px] font-mono uppercase tracking-widest text-[#9AD7FF]">
                          <Globe size={8} /> {(c.file_format || 'web').toUpperCase()} · Umbrales adaptativos
                        </span>
                      )}
                      {c.detector_conflict && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F4B942]/10 border border-[#F4B942]/20 text-[9px] font-mono uppercase tracking-widest text-[#F4B942]">
                          <GitMerge size={8} /> Arbitración IA activa
                        </span>
                      )}
                      {flags.includes('ela_ai_consensus_override') && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#67E8F9]/10 border border-[#67E8F9]/20 text-[9px] font-mono uppercase tracking-widest text-[#67E8F9]">
                          <Zap size={8} /> Override consenso IA
                        </span>
                      )}
                      {flags.includes('copy_move_texture_warning') && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F4D03F]/10 border border-[#F4D03F]/20 text-[9px] font-mono uppercase tracking-widest text-[#F4D03F]">
                          <Filter size={8} /> Textura natural filtrada
                        </span>
                      )}
                      {flags.includes('webp_ela_soft_cap') && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#9AD7FF]/10 border border-[#9AD7FF]/20 text-[9px] font-mono uppercase tracking-widest text-[#9AD7FF]">
                          <Shield size={8} /> Cap ELA 55% (web)
                        </span>
                      )}
                      {(flags.includes('copy_move_fraud_rotational') || flags.includes('copy_move_orb_detected')) && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF7A7A]/10 border border-[#FF7A7A]/25 text-[9px] font-mono uppercase tracking-widest text-[#FF7A7A]">
                          <GitCompareArrows size={8} /> ORB Copy-Move{flags.includes('copy_move_fraud_rotational') ? ' · Fraude rotacional' : ' · Detectado'}
                        </span>
                      )}
                      {flags.includes('inpainting_generativo') && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#a78bfa]/10 border border-[#a78bfa]/25 text-[9px] font-mono uppercase tracking-widest text-[#a78bfa]">
                          <Paintbrush size={8} /> Inpainting generativo detectado
                        </span>
                      )}
                      {flags.includes('api_advisory_evidence') && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F4B942]/10 border border-[#F4B942]/20 text-[9px] font-mono uppercase tracking-widest text-[#F4B942]">
                          <Scale size={8} /> Modo legal · APIs solo contextuales
                        </span>
                      )}
                      {flags.includes('reverse_image_manipulation') && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#FF5A6E]/10 border border-[#FF5A6E]/25 text-[9px] font-mono uppercase tracking-widest text-[#FF5A6E]">
                          <Globe size={8} /> Fake site · búsqueda inversa
                        </span>
                      )}
                      {flags.includes('reverse_image_multiple_versions') && !flags.includes('reverse_image_manipulation') && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F4D03F]/10 border border-[#F4D03F]/25 text-[9px] font-mono uppercase tracking-widest text-[#F4D03F]">
                          <Globe size={8} /> Múltiples versiones detectadas
                        </span>
                      )}
                    </div>
                  );
                })()}
                {scan.is_screenshot && <div data-testid="screenshot-notice" className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#DEFF9A]">Captura de pantalla · Peso ELA reducido</div>}
                {scan.screenshot_crop?.cropped && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#DEFF9A]/70">
                      Auto-recortada: {scan.screenshot_crop.removed_regions?.join(" · ")}
                    </span>
                    <span className="text-[10px] font-mono text-white/30">
                      {scan.screenshot_crop.original_size?.[0]}×{scan.screenshot_crop.original_size?.[1]} → {scan.screenshot_crop.cropped_size?.[0]}×{scan.screenshot_crop.cropped_size?.[1]}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Breakdown vectors */}
            {!isLocked && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-6">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/30 mb-1">Vectores de manipulación</div>
                <div className="font-display text-lg font-bold text-white mb-5">{mediaTypeLabel[scan.media_type] || scan.media_type}</div>
                <div className="space-y-4">
                  {breakdown.map(({ icon: Icon, label, value, hint }) => {
                    const intensity = value > 65 ? "#FF5A6E" : value > 35 ? "#F4D03F" : "#8BF56C";
                    return (
                      <div key={label}>
                        <div className="flex items-center gap-3 mb-1.5">
                          <Icon size={14} className="text-white/40 shrink-0" />
                          <span className="text-[13px] font-medium flex-1 text-white/80">{label}</span>
                          <span className="font-mono text-[13px] font-bold" style={{ color: intensity }}>{value}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${value}%`, background: intensity }} />
                        </div>
                        <p className="text-[10px] font-mono text-white/25 uppercase tracking-widest mt-1">{hint}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* File info */}
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 flex items-center gap-3">
              <FileText size={15} className="text-white/30 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-medium text-white/80 truncate">{scan.original_filename}</div>
                <div className="text-[10px] font-mono text-white/30 mt-0.5">{mediaTypeLabel[scan.media_type] || scan.media_type} · {ts.toLocaleString("es-ES")}</div>
              </div>
            </div>

            {/* PDF block — behaviour differs by mode */}
            {!isLocked && scan.analysis_mode === "verify" && (
              <div className="rounded-2xl border border-[#DEFF9A]/20 bg-[#DEFF9A]/[0.03] p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Download size={13} className="text-[#DEFF9A]" />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#DEFF9A]">Informe de verificación PDF</span>
                </div>
                <p className="text-[12px] text-white/40 leading-relaxed mb-4">Descarga el informe completo con todos los vectores de análisis. No requiere validación adicional.</p>
                <button onClick={downloadPdfDirect}
                  className="w-full font-bold rounded-xl px-5 py-3 transition-all flex items-center justify-center gap-2 text-[13px] bg-[#DEFF9A] hover:bg-[#ecffb5] text-black">
                  <Download size={14} /> Descargar Informe PDF
                </button>
              </div>
            )}
            {!isLocked && scan.analysis_mode === "legal" && (
              <div data-testid="email-report-block" className="rounded-2xl border border-[#818CF8]/20 bg-[#818CF8]/[0.03] p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Send size={13} className="text-[#818CF8]" />
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#818CF8]">Solicitar Informe Pericial</span>
                </div>
                <p className="text-[12px] text-white/40 leading-relaxed mb-4">El informe pericial certificado será procesado por el equipo ScanEye y enviado directamente para su uso en dictamen o expediente judicial.</p>
                <form onSubmit={handleSendEmailReport} className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Mail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25" />
                    <input data-testid="email-report-input" type="email" required value={reportEmail}
                      onChange={e => setReportEmail(e.target.value)} placeholder="despacho@legal.com"
                      className="se-input w-full bg-white/[0.04] border border-white/[0.07] rounded-xl pl-9 pr-3 py-2.5 text-[13px] text-white placeholder:text-white/20 outline-none focus:border-[#818CF8]/40 transition-all" />
                  </div>
                  <button data-testid="email-report-submit" type="submit" disabled={sendingEmail}
                    className="font-bold rounded-xl px-5 py-2.5 transition-all flex items-center justify-center gap-2 text-[13px] disabled:opacity-40 shrink-0 bg-[#818CF8] hover:bg-[#9DA5FB] text-white">
                    {sendingEmail ? <Loader2 size={14} className="animate-spin" /> : <Send size={13} />}
                    {sendingEmail ? "Enviando..." : "Solicitar"}
                  </button>
                </form>
                {emailSentTo && (
                  <p data-testid="email-report-confirmation" className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#8BF56C] flex items-center gap-1.5">
                    <CheckCircle2 size={11} /> Solicitud enviada · Te contactaremos en breve
                  </p>
                )}
              </div>
            )}

            {/* Unlock gate */}
            {isLocked && (
              <div data-testid="unlock-gate" className="rounded-2xl border-2 border-[#DEFF9A]/25 bg-[#DEFF9A]/[0.03] p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#DEFF9A]/10 border border-[#DEFF9A]/20 flex items-center justify-center"><Lock size={16} className="text-[#DEFF9A]" /></div>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-[#DEFF9A]">Análisis preliminar completado</span>
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-white leading-tight mb-2">Desbloquea el Índice de <span className="text-[#DEFF9A]">Veracidad Avanzado</span></h2>
                <p className="text-[13px] text-white/40 leading-relaxed mb-5">Introduce tu email para acceder al <span className="text-white">Índice Ponderado</span> y el <span className="text-white">Informe Forense detallado</span>.</p>
                <form onSubmit={handleUnlock} className="space-y-3">
                  <div className="relative">
                    <Mail size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/25" />
                    <input data-testid="unlock-email-input" type="email" required value={unlockEmail}
                      onChange={e => setUnlockEmail(e.target.value)} placeholder="tu@despacho-legal.com"
                      className="se-input w-full bg-white/[0.04] border border-white/[0.07] rounded-xl pl-11 pr-4 py-3 text-white placeholder:text-white/20 outline-none focus:border-[#DEFF9A]/40 transition-all" />
                  </div>
                  <button data-testid="unlock-submit-btn" type="submit" disabled={unlocking}
                    className="w-full bg-[#DEFF9A] hover:bg-[#ecffb5] text-black font-bold rounded-xl py-3 transition-all flex items-center justify-center gap-2 disabled:opacity-40">
                    {unlocking ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />}
                    {unlocking ? "Verificando..." : "Desbloquear Índice Avanzado"}
                  </button>
                </form>
                <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-white/[0.03] border border-white/[0.05]">
                  <Info size={11} className="text-white/20 shrink-0 mt-0.5" />
                  <p className="text-[10px] text-white/35 leading-relaxed font-mono">Solo emails profesionales · Sin correos temporales · <span className="text-[#DEFF9A]">Cupón 50% en tu primer informe</span></p>
                </div>
              </div>
            )}

            {/* Privacy video */}
            {scan.media_type === "video" && (
              <div data-testid="privacy-notice" className="rounded-xl border border-[#DEFF9A]/15 bg-[#DEFF9A]/[0.03] p-4 flex items-start gap-3">
                <ShieldOff size={15} className="text-[#DEFF9A] shrink-0 mt-0.5" />
                <div>
                  <div className="text-[12px] font-semibold text-[#DEFF9A] mb-0.5">Privacidad protegida</div>
                  <p className="text-[11px] text-white/35 leading-relaxed">El video se analizó en memoria y se eliminó inmediatamente. Solo se conservan metadatos.</p>
                </div>
              </div>
            )}

            {/* Conflict Resolution explanation card */}
            {!isLocked && (() => {
              const c = scan.breakdown?.weighted_components || {};
              if (!c.detector_conflict) return null;
              const flags = c.flags || [];
              const isConsensus = flags.includes('ela_ai_consensus_override');
              const isSingle   = flags.includes('ela_single_detector_override');
              const fmt = (c.file_format || '').toUpperCase();
              return (
                <div className="rounded-2xl border border-[#F4B942]/25 bg-[#F4B942]/[0.04] p-5">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-7 h-7 rounded-lg bg-[#F4B942]/15 border border-[#F4B942]/25 flex items-center justify-center">
                      <GitMerge size={13} className="text-[#F4B942]" />
                    </div>
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4B942]">Arbitración de conflicto activa</div>
                      <div className="text-[9px] font-mono text-white/25 uppercase tracking-widest">Motor v3.5 · Resolución difusa</div>
                    </div>
                  </div>
                  <p className="text-[12px] text-white/60 leading-relaxed mb-3">
                    {isConsensus
                      ? "El Análisis de compresión (ELA) detectó discrepancias, pero "
                        + "Sightengine y Gemini Vision coinciden en alta autenticidad. "
                        + "El sistema ha aplicado un floor de consenso en lugar de emitir un veredicto manipulado falso."
                      : isSingle
                      ? "Un detector de IA indica alta autenticidad pese a las discrepancias ELA. "
                        + "El motor aplica un ajuste parcial de consenso."
                      : "Detectores con se\u00f1ales contradictorias. El motor aplica arbitración difusa."
                    }
                    {c.soft_thresholds && fmt ? ` El formato ${fmt} usa umbrales adaptativos (cap 55% en lugar de 40%).` : ""}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.07] text-white/35">Score ponderado: {scan.truthfulness_score}%</span>
                    {c.ela_cap_value && <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.07] text-white/35">Cap ELA: {c.ela_cap_value}% ({c.cap_tier})</span>}
                  </div>
                </div>
              );
            })()}

            {/* Engine Improvements v3.5 */}
            {!isLocked && (() => {
              const c = scan.breakdown?.weighted_components || {};
              const improvements = [
                {
                  icon: GitCompareArrows,
                  color: "#FF7A7A",
                  title: "ORB Copy-Move rotacional",
                  desc: "Detector de clones invariante a rotación, escala y espejo. Encuentra cópias que el detector dHash no puede ver — el 90% de las manipulaciones reales de Photoshop/GIMP.",
                  active: (c.flags || []).includes('copy_move_fraud_rotational') || (c.flags || []).includes('copy_move_orb_detected'),
                },
                {
                  icon: Paintbrush,
                  color: "#a78bfa",
                  title: "Inpainting generativo",
                  desc: "Detecta relleno de IA (Stable Diffusion, Adobe Generative Fill, DALL\u00b7E outpainting) por análisis de varianza de ruido local. No produce bloques duplicados, por eso Copy-Move no lo detecta.",
                  active: (c.flags || []).includes('inpainting_generativo'),
                },
                {
                  icon: Scale,
                  color: "#818CF8",
                  title: "Modo Legal — sin sesgo de API",
                  desc: "En análisis legal, Gemini y Sightengine se ejecutan y aparecen en el informe como contexto pericial, pero sus probabilidades NO influyen en el veredicto. Solo señales técnicas reproducibles.",
                  active: (c.flags || []).includes('api_advisory_evidence') || scan.analysis_mode === 'legal',
                },
                {
                  icon: GitMerge,
                  color: "#F4B942",
                  title: "Arbitración de conflicto",
                  desc: "Cuando Sightengine+Gemini dicen auténtico pero ELA dispara, el motor vota por consenso en lugar de emitir un falso MANIPULADO.",
                  active: c.detector_conflict,
                },
                {
                  icon: Globe,
                  color: "#9AD7FF",
                  title: "Umbrales adaptativos por formato",
                  desc: "WebP, AVIF e imágenes pequeñas de plataforma usan cap ELA del 55% (en lugar de 40%) porque la recompresión de red social genera artefactos ELA normales.",
                  active: c.web_compressed || c.soft_thresholds,
                },
                {
                  icon: Database,
                  color: "#8BF56C",
                  title: "EXIF contextualizado",
                  desc: "La ausencia de EXIF en archivos web/mensajería (WhatsApp, Twitter, Telegram) ya NO se penaliza con el 30% habitual. Es comportamiento normal de plataforma.",
                  active: (c.flags || []).includes('exif_absent_web_format'),
                },
                {
                  icon: Filter,
                  color: "#F4D03F",
                  title: "Filtro de textura natural",
                  desc: "El detector Copy-Move ahora distingue arquitectura, ventanas simétricas y paredes de ladrillo de una clonación real, evitando falsas alarmas.",
                  active: (c.flags || []).includes('copy_move_texture_warning'),
                },
                {
                  icon: HelpCircle,
                  color: "#F4B942",
                  title: "Veredicto INCONCLUYENTE",
                  desc: "Cuando los detectores están en conflicto y el score está en zona gris (35-58%), el motor ya no fuerza un binario falso: emite INCONCLUYENTE y pide revisión humana.",
                  active: scan.verdict === 'inconclusive',
                },
              ];
              return (
                <details className="group rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none select-none hover:bg-white/[0.02] transition-colors">
                    <div className="flex items-center gap-2.5">
                      <Cpu size={13} className="text-white/30" />
                      <span className="text-[11px] font-mono uppercase tracking-widest text-white/40">Motor forense v3.5 · Mejoras anti-falsos-positivos</span>
                    </div>
                    <ChevronRight size={12} className="text-white/20 transition-transform duration-200 group-open:rotate-90" />
                  </summary>
                  <div className="px-5 pb-5 grid sm:grid-cols-2 gap-3 border-t border-white/[0.05] pt-4">
                    {improvements.map(({ icon: Ic, color, title, desc, active }) => (
                      <div key={title}
                        className="rounded-xl p-3.5 border transition-all"
                        style={{
                          background: active ? `${color}07` : 'rgba(255,255,255,0.02)',
                          borderColor: active ? `${color}25` : 'rgba(255,255,255,0.05)',
                        }}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <Ic size={12} style={{ color: active ? color : '#3A3F46' }} />
                          <span className="text-[11px] font-semibold" style={{ color: active ? color : '#5A5F68' }}>{title}</span>
                          {active && <span className="ml-auto text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full" style={{ background: `${color}15`, color }}>• activo</span>}
                        </div>
                        <p className="text-[10px] text-white/35 leading-relaxed">{desc}</p>
                      </div>
                    ))}
                  </div>
                </details>
              );
            })()}

            {/* Legal */}
            <div data-testid="legal-disclaimer" className="rounded-xl border border-white/[0.05] bg-white/[0.015] p-4 flex items-start gap-3">
              <Info size={12} className="text-white/20 shrink-0 mt-0.5" />
              <p className="text-[11px] text-white/30 leading-relaxed"><span className="font-mono uppercase tracking-widest text-white/50">Aviso legal · </span>Análisis técnico de probabilidad. <span className="text-white/60">No constituye sentencia judicial.</span> Para validaciones legales, genera el Informe Pericial Certificado y consulta con un perito forense homologado.</p>
            </div>
          </div>

          {/* ══════ RIGHT — Sticky forensic panels ══════ */}
          <aside className="space-y-4 lg:sticky lg:top-20">

            {/* ── ELA image ── */}
            {scan.media_type === "image" && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] overflow-hidden">
                <div className="px-4 py-3 border-b border-white/[0.05] flex items-center gap-2.5">
                  <Sparkles size={13} className="text-[#DEFF9A]" />
                  <div>
                    <div className="text-[12px] font-semibold text-white">Mapa ELA</div>
                    <div className="text-[9px] font-mono text-white/25 uppercase tracking-widest">Error Level Analysis q=90 ×20</div>
                  </div>
                </div>
                <div className="bg-black">
                  {elaUrl
                    ? <img src={elaUrl} alt="ELA" className="w-full object-contain max-h-56" />
                    : <div className="h-36 flex items-center justify-center text-white/20 text-[11px] font-mono">Sin mapa ELA</div>
                  }
                </div>
                <div className="px-4 py-3 text-[10px] text-white/30 leading-relaxed font-mono">
                  {(() => {
                    const c = scan.breakdown?.weighted_components || {};
                    if (c.web_compressed) return `Formato ${(c.file_format || 'web').toUpperCase()}: artefactos de compresión de plataforma son NORMALES. Umbral adaptativo aplicado (${c.ela_cap_value ?? 55}%).`;
                    return "Zonas brillantes = regiones con respuesta anómala a la recompresión JPEG. Alta concentración indica posible manipulación.";
                  })()}
                </div>
              </div>
            )}

            {/* ── Heatmap ── */}
            {heatmapUrl && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] overflow-hidden">
                <div className="px-4 py-3 border-b border-white/[0.05] flex items-center gap-2.5">
                  <Thermometer size={13} className="text-[#F4D03F]" />
                  <div>
                    <div className="text-[12px] font-semibold text-white">Mapa de calor</div>
                    <div className="text-[9px] font-mono text-white/25 uppercase tracking-widest">Noise heatmap · sensor PRNU</div>
                  </div>
                </div>
                <div className="bg-black">
                  <img src={heatmapUrl} alt="Heatmap" className="w-full object-contain max-h-56" />
                </div>
                <div className="px-4 py-3 text-[10px] text-white/30 leading-relaxed font-mono">
                  Zonas cálidas indican inconsistencias en el patrón de ruido del sensor. Regiones rojas = mayor sospecha.
                </div>
              </div>
            )}

            {/* ── Analysis params ── */}
            <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/30 mb-3">Parámetros del análisis</div>
              <div className="space-y-2">
                {(() => {
                  const c = scan.breakdown?.weighted_components || {};
                  const rows = [
                    ["Motor",    scan.analysis_mode === "verify" ? "Verify profesional" : "Legal pericial"],
                    ["Sector",   scan.sector || "law"],
                    ["Confianza",scan.confidence_level || "—"],
                    ["Tipo",     mediaTypeLabel[scan.media_type] || scan.media_type],
                    ["Fecha",    ts.toLocaleString("es-ES")],
                    ...(c.file_format ? [["Formato", c.file_format.toUpperCase()]] : []),
                    ...(c.cap_tier    ? [["Cap ELA",  `${c.ela_cap_value ?? '?'}% (${c.cap_tier})`]] : []),
                    ...(c.detector_conflict ? [["Arbitración", "✓ Conflicto resuelto"]] : []),
                  ];
                  return rows.map(([k, val]) => (
                    <div key={k} className="flex items-center justify-between gap-2 py-1.5 border-b border-white/[0.04] last:border-0">
                      <span className="text-[11px] font-mono text-white/30 uppercase tracking-widest shrink-0">{k}</span>
                      <span className="text-[11px] font-mono text-white/70 text-right break-all"
                        style={k === 'Arbitración' ? { color: '#F4B942' } : k === 'Formato' ? { color: '#9AD7FF' } : {}}>
                        {val}
                      </span>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* ── Hash SHA-256 ── */}
            {scan.file_hash && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <KeyRound size={13} className="text-[#DEFF9A]" />
                    <span className="text-[11px] font-mono uppercase tracking-widest text-white/60">Hash SHA-256</span>
                  </div>
                  <button onClick={copyHash}
                    className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-white/[0.08] hover:border-[#DEFF9A]/40 text-white/40 hover:text-[#DEFF9A] text-[9px] font-mono uppercase tracking-widest transition-all">
                    {copied ? <><Check size={9} /> Copiado</> : <><Copy size={9} /> Copiar</>}
                  </button>
                </div>
                <code className="block font-mono text-[10px] text-[#DEFF9A] break-all leading-loose bg-black/40 rounded-xl p-3 border border-white/[0.04]">{scan.file_hash}</code>
              </div>
            )}

            {/* ── EXIF ── */}
            {!isLocked && scan.media_type === "image" && scan.exif_metadata && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4" data-testid="exif-panel">
                <div className="flex items-center gap-2 mb-3">
                  <Database size={13} className="text-white/40" />
                  <span className="text-[11px] font-mono uppercase tracking-widest text-white/40">Metadatos EXIF</span>
                </div>
                <div className="max-h-48 overflow-y-auto scrollbar-thin">
                  {exifEntries.status
                    ? <div className="text-[#F4D03F] text-[11px] flex gap-1.5"><AlertTriangle size={12}/>{exifEntries.status}</div>
                    : <MetaTable rows={exifEntries.items.map(([k,val]) => [k, typeof val==="object"&&val!==null?JSON.stringify(val):String(val)])} />
                  }
                </div>
              </div>
            )}

            {/* ── PDF metadata ── */}
            {!isLocked && scan.media_type === "document" && scan.pdf_metadata && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4" data-testid="pdf-metadata-panel">
                <div className="flex items-center gap-2 mb-3"><FileText size={13} className="text-white/40" /><span className="text-[11px] font-mono uppercase tracking-widest text-white/40">Metadatos PDF</span></div>
                {scan.pdf_metadata._status
                  ? <div className="text-[#F4D03F] text-[11px] flex gap-1.5"><AlertTriangle size={12}/>{scan.pdf_metadata._status}</div>
                  : <MetaTable rows={[["Autor",scan.pdf_metadata.author||"—"],["Software",scan.pdf_metadata.creator||"—"],["Páginas",String(scan.pdf_metadata.pages??"—")],["Creación",scan.pdf_metadata.creation_date||"—"],["Cifrado",scan.pdf_metadata.encrypted?"Sí":"No"]]} />
                }
              </div>
            )}

            {/* ── Audio metadata ── */}
            {!isLocked && scan.media_type === "audio" && scan.audio_metadata && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4" data-testid="audio-metadata-panel">
                <div className="flex items-center gap-2 mb-3"><AudioLines size={13} className="text-white/40" /><span className="text-[11px] font-mono uppercase tracking-widest text-white/40">Metadatos Audio</span></div>
                {scan.audio_metadata._status
                  ? <div className="text-[#F4D03F] text-[11px] flex gap-1.5"><AlertTriangle size={12}/>{scan.audio_metadata._status}</div>
                  : <MetaTable rows={[["Formato",scan.audio_metadata.format||"—"],["Duración",String(scan.audio_metadata.duration_sec??"—")+" s"],["Codec",scan.audio_metadata.codec||"—"],["Bitrate",String(scan.audio_metadata.bitrate??"—")]]} />
                }
              </div>
            )}

            {/* ── Video metadata ── */}
            {!isLocked && scan.media_type === "video" && scan.video_metadata && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4" data-testid="video-metadata-panel">
                <div className="flex items-center gap-2 mb-3"><Scan size={13} className="text-white/40" /><span className="text-[11px] font-mono uppercase tracking-widest text-white/40">Metadatos Video</span></div>
                {scan.video_metadata._status
                  ? <div className="text-[#F4D03F] text-[11px] flex gap-1.5"><AlertTriangle size={12}/>{scan.video_metadata._status}</div>
                  : <MetaTable rows={[["Formato",scan.video_metadata.format||"—"],["Duración",String(scan.video_metadata.duration_sec||"—")],["Resolución",`${scan.video_metadata.width||"—"}×${scan.video_metadata.height||"—"}`],["Encoder",scan.video_metadata.encoder||"—"]]} />
                }
              </div>
            )}

            {/* ── TSA ── */}
            {scan.tsa_timestamp?.status === "granted" && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4">
                <div className="flex items-center gap-2 mb-3"><Clock size={13} className="text-[#9AD7FF]" /><span className="text-[11px] font-mono uppercase tracking-widest text-white/40">TSA · RFC 3161</span></div>
                <MetaTable rows={[["Estado",scan.tsa_timestamp.status],["Fecha UTC",scan.tsa_timestamp.timestamp_utc||"—"],["Proveedor",scan.tsa_timestamp.tsa_provider||"—"]]} />
              </div>
            )}

            {/* ── C2PA ── */}
            {scan.c2pa_credentials?.found && (
              <div className="rounded-2xl border border-white/[0.07] bg-white/[0.025] p-4">
                <div className="flex items-center gap-2 mb-3"><Layers size={13} className="text-[#9AD7FF]" /><span className="text-[11px] font-mono uppercase tracking-widest text-white/40">C2PA · ISO 21617</span></div>
                <MetaTable rows={[["Método",scan.c2pa_credentials.method||"—"],["Proveedor",scan.c2pa_credentials.provider||"—"]]} />
              </div>
            )}

            {/* ── Gemini analysis panel ── */}
            {!isLocked && scan.gemini?.status === "success" && (() => {
              const gm = scan.gemini;
              const manipPct  = Math.round((gm.manipulation_probability ?? 0) * 100);
              const combinedPct = Math.round((gm.combined_probability ?? gm.manipulation_probability ?? 0) * 100);
              const confPct   = Math.round((gm.confidence ?? 0) * 100);
              const gmColor   = combinedPct >= 60 ? "#FF5A6E" : combinedPct >= 30 ? "#F4D03F" : "#8BF56C";
              const verdictEs = {
                likely_authentic:   "Probablemente auténtico",
                suspicious:         "Sospechoso",
                likely_manipulated: "Probable manipulación",
                ai_generated:       "Generado por IA",
                deepfake:           "Deepfake detectado",
              }[gm.verdict] ?? gm.verdict ?? "—";
              const imageTypeLabel = {
                portrait:              "Retrato / cara",
                document:              "Documento oficial",
                screenshot_chat:       "Captura de conversación",
                accident_scene:        "Escena / siniestro",
                ai_generated_candidate:"Candidato IA (resolución/nombre)",
                scene:                 "Escena general",
                unknown:               "Tipo no determinado",
              }[gm.detected_image_type] ?? gm.detected_image_type;
              return (
                <div className="rounded-2xl border bg-white/[0.025] p-4" style={{ borderColor: `${gmColor}30` }}>
                  {/* Header */}
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles size={13} style={{ color: gmColor }} />
                    <span className="text-[11px] font-mono uppercase tracking-widest text-white/40">Gemini Vision IA</span>
                    <span className="ml-auto text-[9px] font-mono text-white/25">{gm.model || "gemini"}</span>
                  </div>

                  {/* Detected image type badge */}
                  {gm.detected_image_type && (
                    <div className="mb-3 flex items-center gap-2">
                      <span className="text-[9px] font-mono uppercase tracking-widest text-white/25">Tipo detectado</span>
                      <span className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full border"
                        style={{ color: gmColor, borderColor: `${gmColor}30`, background: `${gmColor}08` }}>
                        {imageTypeLabel}
                      </span>
                    </div>
                  )}

                  {/* Combined risk bar */}
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[9px] font-mono uppercase tracking-widest text-white/30">Riesgo combinado</span>
                      <span className="font-mono text-[13px] font-bold" style={{ color: gmColor }}>{combinedPct}%</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: `${combinedPct}%`, background: gmColor }} />
                    </div>
                  </div>

                  {/* Verdict + scores row */}
                  <div className="flex items-center gap-2 mb-3 p-2.5 rounded-lg bg-white/[0.03] border border-white/[0.04]">
                    <div className="flex-1 min-w-0">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-white/25 mb-0.5">Veredicto</div>
                      <div className="text-[12px] font-semibold truncate" style={{ color: gmColor }}>{verdictEs}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-white/25 mb-0.5">Confianza</div>
                      <div className="font-mono text-[12px] text-white/70">{confPct}%</div>
                    </div>
                  </div>

                  {/* Reasons list */}
                  {gm.reasons?.length > 0 && (
                    <ul className="space-y-1 mb-3">
                      {gm.reasons.map((r, i) => (
                        <li key={i} className="flex items-start gap-2 text-[11px] text-white/60 leading-relaxed">
                          <span className="shrink-0 mt-0.5" style={{ color: gmColor }}>›</span>{r}
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Lighting notes */}
                  {gm.lighting_notes && (
                    <div className="mt-2 p-2.5 rounded-lg bg-white/[0.03] border border-white/[0.04]">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-white/25 mb-1">Coherencia lumínica</div>
                      <p className="text-[11px] text-white/55 leading-relaxed">{typeof gm.lighting_notes === "string" ? gm.lighting_notes : JSON.stringify(gm.lighting_notes)}</p>
                    </div>
                  )}

                  {/* Blend boundary (deepfake) */}
                  {gm.blend_boundary && (
                    <div className="mt-2 p-2.5 rounded-lg bg-[#FF5A6E]/[0.05] border border-[#FF5A6E]/20">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#FF5A6E]/60 mb-1">Frontera de fusión detectada</div>
                      <p className="text-[11px] text-white/55 leading-relaxed">{typeof gm.blend_boundary === "string" ? gm.blend_boundary : JSON.stringify(gm.blend_boundary)}</p>
                    </div>
                  )}

                  {/* Suspicious region */}
                  {gm.manipulation_region && (
                    <div className="mt-2 p-2.5 rounded-lg bg-[#FF5A6E]/[0.05] border border-[#FF5A6E]/20">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#FF5A6E]/60 mb-1">Región sospechosa</div>
                      <p className="text-[11px] text-white/55 leading-relaxed">{typeof gm.manipulation_region === "string" ? gm.manipulation_region : JSON.stringify(gm.manipulation_region)}</p>
                    </div>
                  )}

                  {/* Background swap notes */}
                  {gm.background_swap_notes && (
                    <div className="mt-2 p-2.5 rounded-lg bg-[#F4B942]/[0.05] border border-[#F4B942]/20">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#F4B942]/60 mb-1">Sustitución de fondo</div>
                      <p className="text-[11px] text-white/55 leading-relaxed">{gm.background_swap_notes}</p>
                    </div>
                  )}

                  {/* Document analysis */}
                  {gm.document_analysis && (
                    <div className="mt-2 p-2.5 rounded-lg bg-[#9AD7FF]/[0.05] border border-[#9AD7FF]/20">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-[#9AD7FF]/60 mb-1">Análisis documental</div>
                      <p className="text-[11px] text-white/55 leading-relaxed">{gm.document_analysis}</p>
                    </div>
                  )}

                  {/* Facial analysis */}
                  {gm.facial_analysis && (
                    <div className="mt-2 p-2.5 rounded-lg bg-white/[0.03] border border-white/[0.04]">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-white/25 mb-1">Análisis facial</div>
                      <p className="text-[11px] text-white/55 leading-relaxed">{typeof gm.facial_analysis === "string" ? gm.facial_analysis : Object.entries(gm.facial_analysis).map(([k,v]) => `${k}: ${v}`).join(" · ")}</p>
                    </div>
                  )}

                  {/* Expanded stats footer */}
                  <div className="mt-3 pt-2.5 border-t border-white/[0.05] grid grid-cols-2 gap-x-3 gap-y-2">
                    {[
                      ["IA generativa",      gm.ai_probability],
                      ["Manipulación",       gm.manipulation_probability],
                      ["Deepfake",           gm.deepfake_probability],
                      ["Face swap",          gm.face_swap_probability],
                      ["Fondo sustituido",   gm.background_swap_probability],
                      ["Doc. manipulado",    gm.document_tampering_probability],
                    ].map(([label, val]) => {
                      const pct = Math.round((val ?? 0) * 100);
                      const c2 = pct >= 60 ? "#FF5A6E" : pct >= 30 ? "#F4D03F" : "#8BF56C";
                      return (
                        <div key={label}>
                          <div className="text-[9px] font-mono uppercase tracking-widest text-white/20 mb-0.5">{label}</div>
                          <div className="font-mono text-[11px]" style={{ color: pct >= 30 ? c2 : undefined, opacity: pct >= 30 ? 1 : 0.4 }}>{pct}%</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* ── Google Vision Web Detection ── */}
            {!isLocked && scan.media_type === "image" && scan.google_vision_web?.status === "success" && (() => {
              const gv = scan.google_vision_web;
              const signalColor = { high: "#FF5A6E", medium: "#F4D03F", low: "#9AD7FF", none: "#8BF56C" }[gv.manipulation_signal] ?? "#8BF56C";
              const signalLabel = {
                high:   "Alta · fake site detectado",
                medium: "Media · múltiples versiones",
                low:    "Baja · imagen encontrada online",
                none:   "Sin señal · imagen no indexada",
              }[gv.manipulation_signal] ?? "—";
              const barWidth = { high: "90%", medium: "60%", low: "30%", none: "5%" }[gv.manipulation_signal] ?? "5%";
              return (
                <div className="rounded-2xl border bg-white/[0.025] p-4" style={{ borderColor: `${signalColor}30` }}>
                  <div className="flex items-center gap-2 mb-3">
                    <Globe size={13} style={{ color: signalColor }} />
                    <span className="text-[11px] font-mono uppercase tracking-widest text-white/40">Google Vision · Búsqueda inversa</span>
                  </div>
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[9px] font-mono uppercase tracking-widest text-white/30">Señal de manipulación</span>
                      <span className="font-mono text-[10px] font-bold" style={{ color: signalColor }}>{signalLabel}</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700" style={{ width: barWidth, background: signalColor }} />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[{ label: "Exactas", value: gv.full_matches }, { label: "Similares", value: gv.partial_matches }, { label: "Páginas", value: gv.pages_found }].map(({ label, value }) => (
                      <div key={label} className="text-center p-2 rounded-lg bg-white/[0.03] border border-white/[0.04]">
                        <div className="text-[9px] font-mono uppercase tracking-widest text-white/25 mb-0.5">{label}</div>
                        <div className="font-mono text-[14px] font-bold text-white">{value ?? 0}</div>
                      </div>
                    ))}
                  </div>
                  {gv.best_guess && (
                    <div className="mb-2 p-2.5 rounded-lg bg-white/[0.03] border border-white/[0.04]">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-white/25 mb-0.5">Identificación</div>
                      <p className="text-[11px] text-white/70">{gv.best_guess}</p>
                    </div>
                  )}
                  {gv.web_entities?.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1">
                      {gv.web_entities.map((e, i) => (
                        <span key={i} className="text-[9px] font-mono px-1.5 py-0.5 rounded-full bg-white/[0.04] border border-white/[0.07] text-white/45">{e.description}</span>
                      ))}
                    </div>
                  )}
                  {gv.manipulation_reason && (
                    <div className="mt-2 p-2.5 rounded-lg border" style={{ background: `${signalColor}08`, borderColor: `${signalColor}20` }}>
                      <p className="text-[10px] leading-relaxed" style={{ color: signalColor }}>{gv.manipulation_reason}</p>
                    </div>
                  )}
                  {gv.urls_sample?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[9px] font-mono uppercase tracking-widest text-white/25 mb-1.5">Páginas donde aparece esta imagen</div>
                      <div className="space-y-1">
                        {gv.urls_sample.map((u, i) => (
                          <a key={i} href={u.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-start gap-1.5 p-1.5 rounded-lg bg-white/[0.03] border border-white/[0.05] hover:border-white/20 hover:bg-white/[0.06] transition-all group">
                            <span className="mt-0.5 text-white/20 group-hover:text-white/40 flex-shrink-0" style={{ fontSize: 9 }}>&#8599;</span>
                            <div className="min-w-0">
                              {u.title && <div className="text-[10px] text-white/60 group-hover:text-white/80 truncate">{u.title}</div>}
                              <div className="text-[9px] text-white/25 truncate">{u.url}</div>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {gv.found_in_fake_site && <span className="text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#FF5A6E]/15 text-[#FF5A6E]">⚠ Fake site</span>}
                    {gv.found_in_news && <span className="text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#8BF56C]/15 text-[#8BF56C]">✓ Fuente noticiosa</span>}
                    {gv.found_in_image_bank && <span className="text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#9AD7FF]/15 text-[#9AD7FF]">· Banco de imágenes</span>}
                    {gv.original_found && !gv.found_in_fake_site && <span className="text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#8BF56C]/15 text-[#8BF56C]">✓ Original localizado</span>}
                  </div>
                </div>
              );
            })()}

            {/* ── Download PDF (sidebar) ── */}
            {!isLocked && (
              <div className="space-y-2">
                {scan.analysis_mode === "verify" ? (
                  <button data-testid="prepare-certificate-btn" onClick={downloadPdfDirect}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-2xl font-bold text-[13px] transition-all bg-[#DEFF9A] hover:bg-[#ecffb5] text-black">
                    <Download size={14} /> Descargar PDF
                  </button>
                ) : (
                  <button data-testid="prepare-certificate-btn" onClick={requestPdfByEmail}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-2xl font-bold text-[13px] transition-all bg-[#818CF8] hover:bg-[#9DA5FB] text-white">
                    <Mail size={14} /> Solicitar Informe Pericial
                  </button>
                )}
                {scan.certificate_text && (
                  <button data-testid="prepare-certificate-txt-btn" onClick={downloadCertificateTxt}
                    className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-2xl border border-white/[0.07] text-white/40 hover:text-white/70 hover:border-white/20 text-[12px] font-mono uppercase tracking-widest transition-all">
                    <FileText size={12} /> Resumen .txt
                  </button>
                )}
              </div>
            )}

          </aside>
        </div>
      </div>

      <ProAccessModal open={showAccess} onClose={() => setShowAccess(false)} source="generate_report" interest="certified_pdf_report" />
    </div>
  );
}

